import Tooltip from 'components/@extended/Tooltip';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftSharedWith } from 'components/drafts/DraftSharedWith';
import { DraftActionsContext, useDraftActions } from 'components/drafts/useDraftActions';
import EmptyState from 'components/EmptyState';
import Loader from 'components/Loader';
import PageHeader from 'components/PageHeader';
import PartyName from 'components/party/PartyName';
import {
    RECORD_PANEL_CLASS,
    RECORD_TAB_STRIP_CLASS,
    RECORD_TABS_CLASS,
    RecordPage,
} from 'components/record-header/RecordHeader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetContactByIdQuery, useUpdatePartyAddressMutation } from 'store/api/contacts';
import { useGetContractsCountQuery } from 'store/api/contracts';
import { useIsEinvoicingEnabled } from 'utils/useIsEinvoicingEnabled';
import { AddressDto, UpdatePartyAddressDto } from 'types/contacts';
import { cn } from 'utils/cn';
import { isApiMissingRecordError } from 'utils/getApiErrorMessage';
import { FeatureFlag } from 'types/features';
import { t } from 'utils/getTranslations';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useLastNameFirst } from 'utils/nameOrder';
import { partyNameString } from 'utils/partyNameUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useDrawsOwnLoadFailure } from 'utils/useDrawsOwnLoadFailure';
import { useHasPermissions } from 'utils/permissionUtil';
import { useGoBack, useLeaveDeletedEntity, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';

import ExclamationMarkIcon from 'assets/images/icons/ExclamationMarkIcon.svg?react';

import { skipToken } from '@reduxjs/toolkit/query';
import TabsGallery, { TabsGalleryRef, TabType } from 'components/TabsGallery';
import PermissionButton from 'components/buttons/PermissionButton';
import DmsLink from 'components/dmslink/DmsLink.tsx';
import NavigationMenu, { NavigationMenuItem } from 'components/navigation/NavigationMenu';
import { SectionEditProvider } from 'components/section-edit/SectionEdit';
import { useGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import BankTransfersTable from 'pages/bank-transfers/BankTransfersTable';
import { ClientAssetsTable } from 'pages/client-assets/ClientAssetsTable';
import { usePartyAssetPermissions } from 'pages/client-assets/usePartyAssetPermissions';
import { DebriefingsView } from 'pages/debriefing/DebriefingsView';
import { useDebriefingPermissions } from 'pages/debriefing/useDebriefingPermissions';
import TasksView from '../matters/views/TasksView.tsx';
import ContactRecordHeader, {
    AddCaseButton,
    ContactChips,
    ContactPartyNumber,
    ContactRelations,
    ContactStateIcons,
} from './ContactRecordHeader';
import BusinessDevelopmentView from './views/EffortView';
import RankingsRefereeView from './views/RankingsRefereeView';
import ExpenseView from './views/ExpenseView';
import ReferralView from './views/ReferralsView';

import CasesView from 'pages/matters/tables/MattersTableContactView';
import BillingView from './views/BillingView';
import ComplianceView from './views/ComplianceView';
import ContractsView from './views/ContractsView';
import EmployeesView from './views/EmployeesView';
import ExperienceRecordsView from './views/ExperienceRecordsView';
import GeneralView from './views/GeneralView';
import InvoiceView from './views/InvoiceView';
import OtherView from './views/OtherView.tsx';
import TimecardView from './views/TimecardView';

import { CoiCheckScopeType } from 'types/coi-check.ts';
import { VerificationStatusType } from 'types/compliance.ts';
import CoiView from '../coi/views/CoiView.tsx';

const USED_CLASSIFIERS = [
    'RELATIONSHIP_TYPE',
    'PARTY_STATUS',
    'PERSON_TITLE',
    'LANGUAGE',
    'BUSINESS_AREA_TYPE',
    'COUNTRY',
    'SERVICE_LEVEL_TYPE',
    'SEGMENT_TYPE',
    'KEYWORD_TYPE',
    'MARKETING_AREA_TYPE',
    'AREA_OF_RESPONSIBILITY_TYPE',
    'MAILING_PREFERENCE_TYPE',
    'COOPERATION_AREA_TYPE',
];

const EFFORT_CHILD_COMPONENT_INDEX = 0;
const REFERRAL_CHILD_COMPONENT_INDEX = 1;
const XR_CHILD_COMPONENT_INDEX = 2;
const NOMINATIONS_CHILD_COMPONENT_INDEX = 3;
const BD_TAB_ID = 'bd';
const COMPLIANCE_TAB_ID = 'compliance-dropdown';
const BILLING_TAB_ID = 'billing-dropdown';
// The classic dropdown tabs' ids: the new bar has no dropdowns, so each opens its first visible child there.
const OLD_DROPDOWN_TABS = {
    [BD_TAB_ID]: ['efforts', 'referrals', 'xr', 'nominations'],
    [COMPLIANCE_TAB_ID]: ['compliance', 'coi'],
};
const tabsGalleryRef = React.createRef<TabsGalleryRef>();

const ADDRESS_FIELD_TO_TYPE: Record<string, string> = {
    registrationAddress: 'REGISTRATION',
    registrationLocalAddress: 'REGISTRATION_LOCAL',
    billingAddress: 'BILLING',
    billingLocalAddress: 'BILLING_LOCAL',
    visitingAddress: 'VISITING',
    homeAddress: 'HOME',
    homeLocalAddress: 'HOME_LOCAL',
    correspondenceAddress: 'CORRESPONDENCE',
};

const NotVerifiedBadge = ({ className }: { className?: string }) => (
    <Tooltip text="compliance.not-verified">
        {/* `!` because a focused menu item recolours every descendant, which would darken the glyph. */}
        <span
            className={cn(
                'bg-error **:text-grey-0! inline-flex h-5 w-[15px] items-center justify-center rounded-[4px]',
                className
            )}
        >
            <ExclamationMarkIcon />
        </span>
    </Tooltip>
);

const ContactPage = () => {
    const { id: contactId } = useParams();

    const navigate = useNavigate();
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/contacts');

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({ codes: USED_CLASSIFIERS });

    // Read afresh: a draft completed in its form opens here, where the form's read of the draft may still be cached.
    const {
        data: contactData,
        isLoading: isLoadingContact,
        error: contactError,
        refetch: refetchContact,
    } = useGetContactByIdQuery(contactId ?? skipToken, { refetchOnMountOrArgChange: true });
    useDrawsOwnLoadFailure('getContactById', contactId, !!contactData);
    const isDraft = !!contactData?.isDraft;
    const leaveDeleted = useLeaveDeletedEntity('/contacts');
    const draftActions = useDraftActions({
        draft: contactData?.isDraft ? { type: 'contact', id: contactData.id } : null,
        createRoute: '/contact/new',
        onDeleted: leaveDeleted,
    });
    const { draft } = draftActions;

    const { data: isEinvoicingEnabled } = useIsEinvoicingEnabled();

    const { data: officeListData, isLoading: isLoadingOfficeList } = useGetMinOfficesWithFiltersQuery(
        { page: 0, pageSize: 2000 },
        {
            skip: contactId === undefined,
        }
    );

    const officeList = useMemo(() => officeListData?.content || [], [officeListData]);

    const { data: contractsCountData, isLoading: isLoadingContractsCount } = useGetContractsCountQuery(
        contactId
            ? { filters: `(parties.party.id=in=(${contactId}),signedByPersonParty.id=in=(${contactId}))` }
            : skipToken
    );

    const hasContractsLinked = useMemo(() => (contractsCountData?.count ?? 0) > 0, [contractsCountData]);

    const [updatePartyAddress] = useUpdatePartyAddressMutation();

    const lastNameFirst = useLastNameFirst();
    const { canRead: canReadDebriefings } = useDebriefingPermissions();
    const { canRead: canReadAssets } = usePartyAssetPermissions();
    // Referees are people: a company never has nominations to list.
    const showsNominations =
        useIsFeatureEnabled(FeatureFlag.RANKINGS_REFEREE) && !!contactData && contactData.partyType !== 'COMPANY';
    const canReadBankTransfers = useHasPermissions(['READ.BANK_TRANSACTIONS.ALL']);
    const isNew = useIsNewDesign();

    const handleAddressUpdate = useCallback(
        async (addressField: string, address: AddressDto) => {
            if (!contactData?.id) return;
            const addressType = ADDRESS_FIELD_TO_TYPE[addressField];
            if (!addressType) return;

            try {
                await updatePartyAddress({
                    id: contactData.id,
                    addressType,
                    ...address,
                } as UpdatePartyAddressDto);
            } catch (error) {
                console.error('Save address error: ', error);
            }
        },
        [contactData, updatePartyAddress]
    );

    const updateBDMenuChildComponentIndex = (index: number) => {
        tabsGalleryRef.current?.updateChildComponentIndex(BD_TAB_ID, index);
    };

    const navigateToEmployeesTab = useCallback(() => {
        if (contactId) {
            navigate(`/contact/${contactId}/employees`);
        }
    }, [contactId, navigate]);

    const bdMenuItems = useMemo<NavigationMenuItem[]>(
        () => [
            {
                handler: () => {
                    updateBDMenuChildComponentIndex(EFFORT_CHILD_COMPONENT_INDEX);
                },
                title: t('bd.bd-efforts'),
            },
            {
                handler: () => {
                    updateBDMenuChildComponentIndex(REFERRAL_CHILD_COMPONENT_INDEX);
                },
                title: t('bd.bd-referrals'),
            },
            {
                handler: () => {
                    updateBDMenuChildComponentIndex(XR_CHILD_COMPONENT_INDEX);
                },
                title: t('bd.bd-experience-records'),
            },
            // A dropdown ignores its children's `hidden`, so the tab's item is left out where the tab is.
            ...(showsNominations
                ? [
                      {
                          handler: () => {
                              updateBDMenuChildComponentIndex(NOMINATIONS_CHILD_COMPONENT_INDEX);
                          },
                          title: t('bd.bd-rankings-referee'),
                      },
                  ]
                : []),
        ],
        [showsNominations]
    );

    const isComplianceNotVerified =
        contactData?.complianceVerificationStatusTypeCode === VerificationStatusType.NOT_VERIFIED;

    const complianceMenuItems = useMemo<NavigationMenuItem[]>(
        () => [
            {
                handler: () => tabsGalleryRef.current?.updateChildComponentIndex(COMPLIANCE_TAB_ID, 0),
                title: t('compliance.compliance'),
                icon: isComplianceNotVerified ? <NotVerifiedBadge /> : undefined,
            },
            {
                handler: () => tabsGalleryRef.current?.updateChildComponentIndex(COMPLIANCE_TAB_ID, 1),
                title: t('coi.coi'),
            },
        ],
        [isComplianceNotVerified]
    );

    const { tabs: TABS, moreTabs } = useMemo((): { tabs: TabType[]; moreTabs?: TabType[] } => {
        if (!contactData) {
            return { tabs: [] };
        }
        const isClient = contactData.relationshipTypeCode === 'CLIENT';
        const general: TabType = {
            id: 'general',
            label: <FormattedMessage id="contacts.general" />,
            component: (
                <GeneralView
                    contactId={contactId}
                    contact={contactData}
                    classifiers={classifiers}
                    updateAddress={handleAddressUpdate}
                    officeList={officeList}
                    onNavigateToEmployeesTab={navigateToEmployeesTab}
                    aside={isDraft ? <DraftSharedWith /> : undefined}
                />
            ),
        };
        const cases: TabType = {
            id: 'cases',
            label: <FormattedMessage id="matters.matters" />,
            component: <CasesView contactIdList={contactId ? [Number(contactId)] : [0]} enableSequenceNumberColumn />,
        };
        const timecards: TabType = {
            id: 'timecards',
            label: <FormattedMessage id="timecards.timecards" />,
            hidden: !isClient && !(contactData.timecardCount && contactData.timecardCount > 0),
            component: <TimecardView contactId={contactId} isInternalContact={contactData.isInternalClient} />,
        };
        const expenses: TabType = {
            id: 'expenses',
            label: <FormattedMessage id="expenses.expenses" />,
            component: <ExpenseView contactId={contactId} />,
        };
        const efforts: TabType = {
            id: 'efforts',
            label: t('bd.bd-efforts'),
            component: <BusinessDevelopmentView key="efforts" contactId={contactId} />,
        };
        const referrals: TabType = {
            id: 'referrals',
            label: t('bd.bd-referrals'),
            component: <ReferralView key="referrals" contactId={contactId} contactName={contactData.name} />,
        };
        const experienceRecords: TabType = {
            id: 'xr',
            label: t('bd.bd-experience-records'),
            component: <ExperienceRecordsView key="xr" contactId={contactId} />,
        };
        const nominations: TabType = {
            id: 'nominations',
            label: t('bd.bd-rankings-referee'),
            tooltip: t('rankings-referees.contact-tab-tooltip'),
            hidden: !showsNominations,
            component: <RankingsRefereeView key="nominations" contactId={contactId} />,
        };
        const tasks: TabType = {
            id: 'tasks',
            label: t('matters.tasks'),
            component: <TasksView clientPartyId={contactId} />,
        };
        const invoices: TabType = {
            id: 'invoices',
            label: <FormattedMessage id="invoices.invoices" />,
            component: <InvoiceView contactId={contactId} />,
            className: 'flex min-h-0 flex-1 flex-col overflow-hidden',
        };
        const debriefings: TabType = {
            id: 'debriefings',
            label: t('debriefing.debriefings'),
            hidden: !canReadDebriefings || !isClient,
            component: <DebriefingsView clientPartyId={contactId} />,
        };
        const assets: TabType = {
            id: 'assets',
            label: t('client-assets.tab'),
            hidden: !canReadAssets,
            component: <ClientAssetsTable variant="client-tab" partyId={contactId} />,
        };
        const bankTransfers: TabType = {
            id: 'bank-transfers',
            label: t('bank-transfers.bank-transfers'),
            hidden: !canReadBankTransfers,
            component: <BankTransfersTable partyId={contactId} />,
        };
        const billing: TabType = {
            id: 'billing',
            label: <FormattedMessage id="contacts.billing-info" />,
            component: (
                <BillingView
                    contactId={contactId}
                    contact={contactData}
                    classifiers={classifiers}
                    isEinvoicingEnabled={Boolean(isEinvoicingEnabled)}
                    updateAddress={handleAddressUpdate}
                />
            ),
        };
        const employees: TabType = {
            id: 'employees',
            label: <FormattedMessage id="employees.employees" />,
            hidden: contactData.partyType !== 'COMPANY',
            component: <EmployeesView contactId={contactId} contact={contactData} />,
        };
        const contracts: TabType = {
            id: 'contracts',
            label: <FormattedMessage id="contracts.contracts" />,
            hidden: !isClient && !hasContractsLinked,
            component: <ContractsView contactId={contactId} contact={contactData} />,
        };
        const compliance: TabType = {
            id: 'compliance',
            label: t('compliance.compliance'),
            hidden: !isClient,
            component: <ComplianceView key="compliance" refetchContact={refetchContact} contact={contactData} />,
        };
        const coi: TabType = {
            id: 'coi',
            label: t('coi.coi'),
            hidden: !isClient,
            component: <CoiView key="coi" scope={CoiCheckScopeType.CLIENT} entityId={contactId} />,
        };
        const other: TabType = {
            id: 'other',
            label: <FormattedMessage id="contacts.other.title" />,
            component: <OtherView contact={contactData} classifiers={classifiers} />,
        };

        // A draft keeps the tabs of its own fields; those that list or add related records wait for the contact.
        if (isDraft) {
            return isNew ? { tabs: [general, billing], moreTabs: [other] } : { tabs: [general, billing, other] };
        }

        if (isNew) {
            return {
                tabs: [
                    general,
                    cases,
                    invoices,
                    contracts,
                    { ...compliance, icon: isComplianceNotVerified ? <NotVerifiedBadge /> : undefined },
                    billing,
                    timecards,
                    efforts,
                    referrals,
                ],
                moreTabs: [
                    expenses,
                    employees,
                    other,
                    tasks,
                    coi,
                    experienceRecords,
                    nominations,
                    debriefings,
                    assets,
                    bankTransfers,
                ],
            };
        }

        const bdTabs = [efforts, referrals, experienceRecords, ...(showsNominations ? [nominations] : [])];
        // A dropdown ignores its children's `hidden`, so it holds only the visible ones, and with none besides billing
        // info, billing stays a plain tab.
        const billingChildren = [
            { tab: billing, menuTitleId: 'contacts.billing-info' },
            { tab: assets, menuTitleId: 'client-assets.records' },
            { tab: bankTransfers, menuTitleId: 'bank-transfers.bank-transfers' },
        ].filter(({ tab }) => !tab.hidden);
        const billingTabs = billingChildren.map(({ tab }) => tab);
        const billingMenuItems = billingChildren.map<NavigationMenuItem>(({ menuTitleId }, index) => ({
            handler: () => tabsGalleryRef.current?.updateChildComponentIndex(BILLING_TAB_ID, index),
            title: t(menuTitleId),
        }));
        return {
            tabs: [
                general,
                cases,
                timecards,
                expenses,
                {
                    id: BD_TAB_ID,
                    label: <NavigationMenu title={t('bd.bd')} menuItems={bdMenuItems} />,
                    displayLabel: <FormattedMessage id="bd.bd" />,
                    isDropdown: true,
                    childTabIds: bdTabs.map((tab) => tab.id),
                    childComponents: bdTabs.map((tab) => tab.component),
                },
                tasks,
                invoices,
                debriefings,
                billingTabs.length > 1
                    ? {
                          id: BILLING_TAB_ID,
                          label: <NavigationMenu title={t('contacts.billing-info')} menuItems={billingMenuItems} />,
                          displayLabel: <FormattedMessage id="contacts.billing-info" />,
                          isDropdown: true,
                          childTabIds: billingTabs.map((tab) => tab.id),
                          childComponents: billingTabs.map((tab) => tab.component),
                      }
                    : billing,
                employees,
                contracts,
                {
                    id: COMPLIANCE_TAB_ID,
                    label: <NavigationMenu title={t('compliance.compliance')} menuItems={complianceMenuItems} />,
                    displayLabel: <FormattedMessage id="compliance.compliance" />,
                    isDropdown: true,
                    hidden: !isClient,
                    // The dropdown tab has no padding of its own, so the badge brings its own inset.
                    icon: isComplianceNotVerified ? <NotVerifiedBadge className="mr-3.5 -ml-1.5" /> : undefined,
                    childTabIds: [compliance.id, coi.id],
                    childComponents: [compliance.component, coi.component],
                },
                other,
            ],
        };
    }, [
        contactData,
        contactId,
        classifiers,
        isEinvoicingEnabled,
        handleAddressUpdate,
        officeList,
        bdMenuItems,
        complianceMenuItems,
        refetchContact,
        hasContractsLinked,
        navigateToEmployeesTab,
        canReadDebriefings,
        canReadAssets,
        canReadBankTransfers,
        showsNominations,
        isComplianceNotVerified,
        isNew,
        isDraft,
    ]);

    if (isLoadingContact || isLoadingClassifiers || isLoadingOfficeList || isLoadingContractsCount) {
        return <Loader />;
    }

    // A missing or forbidden contact is on its way to /404.
    if (isNew && contactError && !contactData && !isApiMissingRecordError(contactError)) {
        return (
            <EmptyState
                variant="error"
                errorTitleId="contacts.error.load-title"
                onRetry={() => void refetchContact()}
                surface="contact-record"
            />
        );
    }

    const contactName = contactData ? partyNameString(contactData, { useOfficialName: true, lastNameFirst }) : '';
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;

    // The views of the tabs read the draft from here: what it misses, and whom it is shared with.
    const tabsGallery = (
        <DraftActionsContext.Provider value={isDraft ? draftActions : null}>
            <TabsGallery
                ref={tabsGalleryRef}
                tabs={TABS}
                moreTabs={moreTabs}
                tabAliases={isNew ? OLD_DROPDOWN_TABS : undefined}
                arrows={isNew ? { back: t('back'), next: t('next') } : undefined}
                tabGroupId="contact-page"
                trackTabsInHistoryState
                usePathBasedRouting
                wrapperClassName={RECORD_TABS_CLASS}
                panelClassName={isNew ? RECORD_PANEL_CLASS : 'min-h-0 flex-1 overflow-y-auto'}
                // The new design's white band runs from the header through the tab strip, edge to edge.
                className={isNew ? RECORD_TAB_STRIP_CLASS : undefined}
            />
            {draftActions.shareDialog}
        </DraftActionsContext.Provider>
    );

    if (isNew && contactData) {
        return (
            <RecordPage
                header={
                    <div className="flex flex-col gap-4">
                        <ContactRecordHeader
                            contact={contactData}
                            classifiers={classifiers}
                            draftActions={draftActions}
                        />
                        {draftNotice}
                    </div>
                }
            >
                <SectionEditProvider canEdit={contactData.isEditable}>{tabsGallery}</SectionEditProvider>
            </RecordPage>
        );
    }

    return (
        <PaddedLayout className="flex h-full flex-col overflow-hidden">
            {contactData ? (
                <>
                    <PageHeader
                        // A draft may be saved before it has a name.
                        title={contactName || t('new-entity.contact')}
                        titleNode={
                            contactName ? (
                                <PartyName party={contactData} variant="readonly-self" personaNonGrataIconSize={24} />
                            ) : undefined
                        }
                        icons={<ContactStateIcons contact={contactData} className="h-[36px] w-[36px] text-[35px]" />}
                        showBreadcrumb
                        onBack={goBack}
                        buttons={
                            <div className="flex items-center justify-end gap-2">
                                {isDraft ? (
                                    <DraftRecordActions actions={draftActions} />
                                ) : (
                                    <>
                                        <PermissionButton
                                            hasAuthority={contactData.isEditable}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => navigateWithReturn(`/contact/${contactId}/edit`)}
                                        >
                                            <FormattedMessage id="edit" />
                                        </PermissionButton>
                                        <AddCaseButton contact={contactData} />
                                    </>
                                )}
                            </div>
                        }
                    >
                        <div className="flex w-full flex-wrap items-center gap-4 py-4 max-sm:gap-2">
                            {isDraft ? <DraftChip /> : null}
                            <ContactChips contact={contactData} classifiers={classifiers} />
                            <ContactPartyNumber contact={contactData} />
                            <ContactRelations contact={contactData} />
                            {draft ? <DraftMeta draft={draft} /> : null}
                            {contactData?.dmsLink ? (
                                <DmsLink link={contactData?.dmsLink} isIconDisplayed={true} />
                            ) : null}
                        </div>
                    </PageHeader>
                    {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
                    {tabsGallery}
                </>
            ) : null}
        </PaddedLayout>
    );
};

export default ContactPage;
